import { useRouter } from "@/_router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/controller";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import Container from "@/components/Container";
import PageHeader from "@/components/PageHeader";
import BackgroundAmbience from "@/components/BackgroundAmbience";
import BottomActionBar from "@/components/BottomActionBar";
import CircularProgress from "@/components/CircularProgress";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
} from "@/components/Icons";
import { recordFeatureUsed } from "@/models/feature-adoption";
import { useStillWorking } from "@/models/useStillWorking";
import { promptAiInstructions } from "@/models/useAiInstructions";
import { actions } from "@/controller";

export default function QuizPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const transcriptId = router.query?.id as string;
  const transcripts = useAppStore((state) => state.transcripts);
  const transcript = useMemo(
    () => transcripts.find((tr) => tr.id === transcriptId),
    [transcripts, transcriptId]
  );

  const [isLoading, setIsLoading] = useState(!transcript?.quiz);
  const stillWorking = useStillWorking(isLoading);


  const [progress, setProgress] = useState(0);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const generationHandleRef = useRef<ReturnType<
    typeof actions.aiContent.generate
  > | null>(null);
  const generationIdRef = useRef(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});

  const _startProgress = () => {
    if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    setProgress(0);
    intervalIdRef.current = setInterval(() => {
      setProgress((prev) => Math.min(99, prev + 3));
    }, 1000);
  };

  useEffect(() => {
    if (transcriptId && !transcript) {
      void actions.transcript.get(transcriptId);
    }
  }, [transcriptId, transcript]);

  useEffect(() => {
    setIsLoading(true);
    setProgress(0);
    setIsScrolled(false);
    setCurrentQuestionIndex(0);
    setAnswers({});
  }, [transcriptId]);

  const generateQuiz = useCallback(async (instructions?: string) => {
    if (!transcriptId) return;
    generationIdRef.current += 1;
    const generationId = generationIdRef.current;
    generationHandleRef.current?.cancel();
    setIsLoading(true);
    _startProgress();
    const handle = actions.aiContent.generate(
      { feature: "quiz", transcriptId, instructions },
      {
      timeoutMs: 10 * 60 * 1_000,
      }
    );
    generationHandleRef.current = handle;
    const completed = await handle.done;
    if (generationId !== generationIdRef.current) return;
    generationHandleRef.current = null;
    // Completion is driven by the poll handle — a store watcher would fire
    // instantly on regeneration while the OLD quiz is still cached.
    if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    setIsLoading(false);
    if (!("error" in completed)) {
      setProgress(100);
      recordFeatureUsed("quiz");
    }
  }, [transcriptId]);

  useEffect(() => {
    if (!transcript || transcript.quiz) return;
    // A custom regeneration clears the stored quiz; the in-flight guard
    // keeps this auto-start from cancelling that run.
    if (generationHandleRef.current?.key === `${transcriptId}:quiz`) return;
    void generateQuiz();
  }, [Boolean(transcript), Boolean(transcript?.quiz), generateQuiz, transcriptId]);

  // Unmount-only: abandon whatever generation/poll is still in flight.
  useEffect(
    () => () => {
      generationIdRef.current += 1;
      generationHandleRef.current?.cancel();
      generationHandleRef.current = null;
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    },
    []
  );

  // Content already present with no generation in flight (page load or
  // transcript switch): drop the spinner. Generation completion itself is
  // handle-driven — the old quiz is still cached during a regeneration.
  useEffect(() => {
    if (transcript?.quiz && isLoading && !generationHandleRef.current) {
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
      setIsLoading(false);
      setProgress(100);
    }
  }, [transcript?.quiz, isLoading]);

  const quiz = transcript?.quiz;
  const questions = quiz?.questions || [];
  const currentQuestion = questions[currentQuestionIndex];
  const selectedOption = answers[currentQuestionIndex];
  const showResult = !!selectedOption;

  useEffect(() => {
    if (questions.length > 0 && currentQuestionIndex >= questions.length) {
      setCurrentQuestionIndex(0);
      setAnswers({});
    }
  }, [questions.length, currentQuestionIndex]);

  const handleOptionClick = (option: string) => {
    if (selectedOption) return;
    setAnswers((prev) => ({ ...prev, [currentQuestionIndex]: option }));
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleRegenerate = () => {
    if (!transcriptId) return;
    // No quiz yet (failed/empty) → plain retry; otherwise regenerating an
    // existing quiz always collects custom instructions first.
    if (!transcript?.quiz) {
      setCurrentQuestionIndex(0);
      setAnswers({});
      void generateQuiz();
      return;
    }
    promptAiInstructions("quiz", (instructions) => {
      setCurrentQuestionIndex(0);
      setAnswers({});
      void generateQuiz(instructions);
    });
  };

  return (
    <Container reading className="fixed inset-0 bg-[var(--bg-primary)] text-[var(--text-primary)] flex flex-col font-sans overflow-hidden">
      <BackgroundAmbience />

      <PageHeader isScrolled={isScrolled} onBack={() => router.back()}>
        <button
          data-action-id="quiz.regenerate"
          onClick={handleRegenerate}
          disabled={isLoading}
          className="icon-btn"
        >
          <SparklesIcon size={18} />
        </button>
      </PageHeader>

      <main
        className="flex-1 relative z-10 overflow-y-auto flex flex-col pb-32 pt-6"
        onScroll={(e) => setIsScrolled(e.currentTarget.scrollTop > 0)}
      >
        <div className="flex-1 flex flex-col px-5 md:px-8 max-w-3xl mx-auto w-full">
        {isLoading || !transcript ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 pb-20">
            <CircularProgress
              progress={progress}
              size="lg"
              indeterminate={stillWorking}
            />
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2 mt-6 tracking-tight">
              {t("transcript.quizHeading")}
            </h3>
            <p className="text-[14px] text-[var(--text-tertiary)] leading-relaxed">
              {stillWorking
                ? t("aiContent.stillWorkingSubtitle")
                : `${t("quiz.generatingQuiz")} ${progress}%`}
            </p>
          </div>
        ) : questions.length > 0 && currentQuestion ? (
          <div className="w-full max-w-md md:max-w-lg mx-auto space-y-6">
            {/* Question Card */}
            <div className="rounded-[32px] bg-[var(--text-primary)] backdrop-blur-md p-8 shadow-sm min-h-[200px] flex flex-col items-center justify-center text-center relative overflow-hidden">
              <span className="text-xs font-bold text-[var(--bg-primary)] uppercase tracking-widest mb-4 opacity-70">
                {t("quiz.question")}
              </span>
              <h2
                dir="auto"
                className="text-xl md:text-2xl font-bold leading-relaxed text-[var(--bg-primary)] relative z-10"
              >
                {currentQuestion.question}
              </h2>
            </div>

            {/* Options */}
            <div className="space-y-3">
              {currentQuestion.options.map((option, idx) => {
                const isSelected = selectedOption === option;
                const isCorrect = option === currentQuestion.correctAnswer;
                const letter = String.fromCharCode(65 + idx);

                let buttonStyle =
                  "glass-tile text-[var(--text-primary)]";
                let circleStyle =
                  "bg-[var(--fill-1)] text-[var(--text-secondary)]";

                if (showResult) {
                  if (isCorrect) {
                    buttonStyle =
                      "bg-[var(--success)] text-white shadow-[0_0_20px_rgba(34,197,94,0.3)]";
                    circleStyle = "bg-white/20 text-white";
                  } else if (isSelected) {
                    buttonStyle =
                      "bg-[var(--destructive)] text-white shadow-[0_0_20px_rgba(239,68,68,0.3)]";
                    circleStyle = "bg-white/20 text-white";
                  } else {
                    buttonStyle =
                      "glass-tile text-[var(--text-tertiary)] opacity-50";
                  }
                } else {
                  buttonStyle =
                    "glass-tile text-[var(--text-primary)] active:scale-[0.98] active:bg-[var(--glass-bg-active)] hover:bg-[var(--glass-bg-hover)]";
                }

                return (
                  <button
                    data-action-id="quiz.answer"
                    key={idx}
                    onClick={() => handleOptionClick(option)}
                    disabled={showResult}
                    className={`w-full p-5 pl-5 rounded-3xl text-lg font-medium transition-all duration-300 flex items-center text-start min-h-[72px] ${buttonStyle}`}
                  >
                    <span
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold me-4 shrink-0 transition-colors ${circleStyle}`}
                    >
                      {letter}
                    </span>
                    <span dir="auto" className="flex-1">
                      {option}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4">
            <div className="glass w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <SparklesIcon size={32} className="text-[var(--text-muted)]" />
            </div>
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">
              {t("quiz.quizFailedDescription")}
            </h3>
            <button
              data-action-id="quiz.regenerate"
              onClick={handleRegenerate}
              className="px-6 py-3 rounded-full bg-[var(--text-primary)] text-[var(--bg-primary)] font-semibold active:scale-95 hover:opacity-90 transition-all"
            >
              {t("quiz.tryAgain")}
            </button>
          </div>
        )}
        </div>
      </main>

      {/* Bottom Navigation */}
      {questions.length > 0 && !isLoading && (
        <BottomActionBar compactBottom className="flex justify-center">
          <div className="glass-thick inline-flex items-center gap-4 p-2 rounded-full">
            <button
              data-action-id="quiz.previous"
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              className="icon-btn icon-btn-quiet w-12 h-12 text-[var(--text-primary)] disabled:opacity-30 active:scale-90 transition-all"
            >
              <ChevronLeftIcon size={24} className="rtl:-scale-x-100" />
            </button>

            <div className="px-2 flex flex-col items-center">
              <span
                dir="ltr"
                className="text-sm font-bold text-[var(--text-primary)] tabular-nums"
              >
                {currentQuestionIndex + 1} / {questions.length}
              </span>
            </div>

            <button
              data-action-id="quiz.next"
              onClick={() => {
                if (currentQuestionIndex < questions.length - 1) {
                  setCurrentQuestionIndex((prev) => prev + 1);
                } else if (showResult) {
                  setCurrentQuestionIndex(0);
                  setAnswers({});
                }
              }}
              disabled={
                currentQuestionIndex === questions.length - 1 && !showResult
              }
              className={`h-12 rounded-full flex items-center justify-center transition-all active:scale-90 ${
                showResult && currentQuestionIndex === questions.length - 1
                  ? "bg-[var(--text-primary)] text-[var(--bg-primary)] shadow-md px-6 w-auto"
                  : "bg-[var(--glass-bg-hover)] hover:bg-[var(--glass-bg-active)] text-[var(--text-primary)] disabled:opacity-30 w-12"
              }`}
            >
              {showResult && currentQuestionIndex === questions.length - 1 ? (
                <span className="text-sm font-bold">
                  {t("transcript.restart")}
                </span>
              ) : (
                <ChevronRightIcon size={24} className="rtl:-scale-x-100" />
              )}
            </button>
          </div>
        </BottomActionBar>
      )}
    </Container>
  );
}
